import { equals } from "ramda";
import { type FC, type PropsWithChildren, useRef } from "react";
import { useIntl } from "react-intl";
import { ZodError } from "zod";

import { createWorkflowEditorStore, WorkflowEditorContext } from "@/modules/workflow/stores/workflow-editor-store.ts";
import { debouncedSave } from "@/modules/workflow/utils/debounceSave.ts";
import { type AutoSaveData, getWorkflowEventInputForSaving } from "@/modules/workflow/utils/generateWorkflow.ts";

export const WorkflowEditorStoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const store = useRef(createWorkflowEditorStore()).current;
  const intl = useIntl();

  // Auto save
  store.subscribe<AutoSaveData | null>(
    (state) => {
      if (state.isLoadingActivities || state.isLoadingWorkflow || state.isDragging) {
        return null;
      }

      return [state.workflowEvent, state.nodes, state.edges, state.parametersRecord, state.sampleTypeId];
    },
    async (data: AutoSaveData | null, previousData: AutoSaveData | null) => {
      if (data == null) {
        // No auto save. It's loading!
        return;
      }

      try {
        // getWorkflowEventInputForSaving uses the schema `workflowEventSchema` that throw with restricted content
        const currentSaveData = getWorkflowEventInputForSaving(data);

        let previousSaveData = null;

        try {
          previousSaveData = getWorkflowEventInputForSaving(previousData);
        } catch {
          // Do nothing. It may have failed due to restricted content or whatever.
          // As long as currentSaveData is valid, let's save.
          // At worse, we save too much, which is ok.
        }

        // This is an acceptable mistake to do it like:
        // Currently we are not comparing the last saved state against the last state to save, but only the previous frame state against the last one.
        // In other words, if you, for instance, move an activity and place it at the exact same place, we should not save it after the debounce time.
        // But we do, because we are comparing with the previous frame position, not the previous saved position.
        // That's ok for now.
        if (!equals(currentSaveData, previousSaveData)) {
          // We block the auto save if a restricted data is detected
          store.setState({ autoSaveErrorMessage: null });

          // All good, let's save
          store.setState({ isSavingWorkflow: true });
          await debouncedSave(currentSaveData, store.setState);
        }
      } catch (error) {
        // ZodError extends Error, so need to be first
        if (error instanceof ZodError) {
          store.setState({
            autoSaveErrorMessage: [...new Set(error.issues.map((error) => error.message))].join(", "),
          });
        } else if (error instanceof Error) {
          store.setState({ autoSaveErrorMessage: error.message });
        } else {
          store.setState({ autoSaveErrorMessage: intl.formatMessage({ id: "an error occurred" }) });
        }

        return;
      }
    },
    { equalityFn: equals },
  );

  return <WorkflowEditorContext.Provider value={store}>{children}</WorkflowEditorContext.Provider>;
};
